import React, { useState, useEffect } from 'react';
import { servicesAPI, professionalsAPI } from '../services/api';
import toast from 'react-hot-toast';

/* ── nicov2 modal styles ───────────────── */
const M = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(15,64,35,.4)', backdropFilter: 'blur(6px)',
    zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px', animation: 'fadeIn .2s ease',
  },
  modal: {
    background: '#fff', borderRadius: '22px', padding: window.innerWidth <= 768 ? '20px' : '32px',
    maxWidth: '560px', margin: '0 auto', width: '100%', maxHeight: '90vh', overflowY: 'auto',
    animation: 'slideUp .3s ease',
    boxShadow: '0 20px 48px rgba(15,64,35,.14)',
    boxSizing: 'border-box',
  },
  head: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800,
    textTransform: 'uppercase', fontSize: '24px', margin: 0,
    letterSpacing: '-0.01em', color: '#14562e',
  },
  close: {
    width: '36px', height: '36px', borderRadius: '10px', background: '#f4ede0',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', border: 'none', transition: 'background 0.15s',
    color: '#183d24',
  },
  label: {
    fontSize: '12px', fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '1px', color: '#6a7268', marginBottom: '6px', display: 'block',
  },
  input: {
    width: '100%', padding: '12px 14px', borderRadius: '10px',
    border: '1.5px solid #e5ddc9', background: '#f4ede0', color: '#183d24',
    fontFamily: "'DM Sans', sans-serif", fontSize: '14.5px',
    transition: 'border-color 0.15s, background 0.15s', outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%', padding: '12px 14px', borderRadius: '10px',
    border: '1.5px solid #e5ddc9', background: '#f4ede0', color: '#183d24',
    fontFamily: "'DM Sans', sans-serif", fontSize: '14.5px',
    cursor: 'pointer', outline: 'none', boxSizing: 'border-box',
  },
  textarea: {
    width: '100%', padding: '12px 14px', borderRadius: '10px',
    border: '1.5px solid #e5ddc9', background: '#f4ede0', color: '#183d24',
    fontFamily: "'DM Sans', sans-serif", fontSize: '14.5px',
    resize: 'vertical', minHeight: '80px', outline: 'none',
    boxSizing: 'border-box',
  },
  grid: {
    display: 'grid', gridTemplateColumns: window.innerWidth <= 480 ? '1fr' : '1fr 1fr', gap: '16px',
    marginTop: '16px',
  },
  row: {
    display: 'flex', flexDirection: 'column', gap: '6px',
  },
  chipRow: {
    display: 'flex', flexWrap: 'wrap', gap: '8px',
  },
  actions: {
    display: 'flex', gap: '10px', marginTop: '22px', justifyContent: 'flex-end',
  },
  btn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: '8px', padding: '12px 22px', borderRadius: '12px',
    fontWeight: 700, fontSize: '14px', cursor: 'pointer', border: 'none',
    fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s',
  },
};

/* ── Fallback service types if API fails ── */
// [NOTA] O campo `icon` aqui embaixo (e o que vem de service_types.icon lá do banco)
// não é mais usado pra desenhar o ícone do chip — ver getServiceTypeIcon() logo
// abaixo, que troca os emojis crus por um ícone de linha (mesmo estilo dos botões do
// topo do VehicleDashboard), escolhido por palavra-chave no NOME do tipo. Deixado aqui
// só porque a API ainda devolve esse campo; não faz mal ficar sem uso.
const FALLBACK_TYPES = [
  { id: 'st_01', name: 'Troca de óleo', icon: '💧', category: 'maintenance' },
  { id: 'st_02', name: 'Pneus', icon: '🛞', category: 'maintenance' },
  { id: 'st_03', name: 'Freios', icon: '🔧', category: 'maintenance' },
  { id: 'st_04', name: 'Revisão', icon: '⚙️', category: 'maintenance' },
  { id: 'st_05', name: 'Bateria', icon: '🔋', category: 'maintenance' },
  { id: 'st_06', name: 'Elétrica', icon: '💡', category: 'maintenance' },
];

// [NEW] Ícones de linha pro chip de "Tipo de serviço" — mesma linguagem visual já usada
// nos botões do topo do VehicleDashboard (viewBox 24x24, stroke=currentColor,
// strokeWidth 2, pontas/junções arredondadas), em vez dos emojis crus que vinham de
// service_types.icon (que rendem diferente e meio sem graça dependendo do sistema).
// currentColor faz o ícone herdar a cor do texto do chip (branco quando selecionado,
// verde escuro quando não) automaticamente, sem precisar de cor própria.
function normalizeTypeName(str) {
  return (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

const TYPE_ICON_SVGS = {
  droplet: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 11-11.31 0z"/></svg>,
  filter: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>,
  battery: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="6" width="18" height="12" rx="2" ry="2"/><line x1="23" y1="13" x2="23" y2="11"/></svg>,
  zap: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  wind: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.59 4.59A2 2 0 1111 8H2m10.59 11.41A2 2 0 1014 16H2m15.73-8.27A2.5 2.5 0 1119.5 12H2"/></svg>,
  paintRoller: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="14" height="6" rx="1"/><line x1="7" y1="9" x2="7" y2="14"/><rect x="5" y="14" width="4" height="7" rx="1"/></svg>,
  disc: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg>,
  octagon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"/></svg>,
  settings: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 15a3 3 0 100-6 3 3 0 000 6z"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>,
  tool: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>,
  plus: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  // [NEW] Mesmo par usado no badge "Foto ✓"/"Vídeo ✓" do histórico (VehicleDashboard.jsx)
  // e do relatório (ReportsPage.jsx) — reaproveitado aqui nos botões de upload, pra não
  // ter emoji 📷/🎥 num lugar e ícone de linha no outro.
  camera: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>,
  video: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>,
};

// Escolhe o ícone por palavra-chave no nome do tipo — funciona pra qualquer tipo
// cadastrado no banco, mesmo sem editar service_types. Ordem importa: checagens mais
// específicas ("filtro") vêm antes de genéricas, pra "Filtro de Ar" não cair em
// nenhuma regra pensada pra "Ar Condicionado", por exemplo. Sem match nenhum, cai na
// chave inglesa (🔧), que já era o fallback visual do app pra manutenção em geral.
function getServiceTypeIcon(name) {
  const n = normalizeTypeName(name);
  if (n.includes('filtro')) return TYPE_ICON_SVGS.filter;
  if (n.includes('oleo')) return TYPE_ICON_SVGS.droplet;
  if (n.includes('bateria')) return TYPE_ICON_SVGS.battery;
  if (n.includes('eletric')) return TYPE_ICON_SVGS.zap;
  if (n.includes('ar condicionado')) return TYPE_ICON_SVGS.wind;
  if (n.includes('funilaria') || n.includes('pintura')) return TYPE_ICON_SVGS.paintRoller;
  if (n.includes('pneu') || n.includes('alinhamento') || n.includes('balanceamento')) return TYPE_ICON_SVGS.disc;
  if (n.includes('freio')) return TYPE_ICON_SVGS.octagon;
  if (n.includes('revis')) return TYPE_ICON_SVGS.settings;
  return TYPE_ICON_SVGS.tool;
}

// ── Limites de mídia de comprovação (foto/vídeo do reparo) ──
// Objetivo: dar prova visual do serviço (útil sobretudo em lanternagem/funilaria, pra
// mostrar que o reparo foi simples) sem deixar o upload virar um jeito de guardar
// arquivo grande de graça. Checado no navegador ANTES de subir pro R2 — não gasta
// banda com um arquivo que vai ser rejeitado.
const MAX_PHOTO_MB = 8;
const MAX_VIDEO_MB = 15;
const MAX_VIDEO_SECONDS = 15;
const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime'];

// Lê a duração de um vídeo local sem precisar subir ele antes — carrega só o
// metadata do arquivo (rápido, não baixa/decodifica o vídeo inteiro).
function readVideoDuration(file) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Não foi possível ler o vídeo'));
    };
    video.src = URL.createObjectURL(file);
  });
}

/* ── Chip component ────────────────────── */
function Chip({ label, icon, selected, onClick }) {
  return (
    <span
      onClick={onClick}
      style={{
        padding: '8px 14px', borderRadius: '999px',
        background: selected ? '#14562e' : '#f4ede0',
        color: selected ? '#fff' : '#183d24',
        fontSize: '13px', fontWeight: 600, cursor: 'pointer',
        border: '1.5px solid transparent',
        transition: 'all 0.15s',
        display: 'inline-flex', alignItems: 'center', gap: '6px',
      }}
    >
      {icon && <span>{icon}</span>}
      {label}
    </span>
  );
}

const emptyForm = {
  vehicle_id: '',
  service_type_id: '',
  custom_service_name: '',
  performed_at: new Date().toISOString().split('T')[0],
  km_at_service: '',
  cost: '',
  professional_name: '',
  professional_id: null,
  notes: '',
  photo_url: '',
  video_url: '',
};

// ── Cost mask helpers ──
// Formata um valor CRU (o que vem do banco, ex: "5000.00", "5000" ou o número 5000 — sem
// separador de milhar, com no máximo um ponto decimal) no mesmo formato mascarado que o
// usuário vê enquanto digita no campo (ex: "5.000,00"). Usado só pra preencher o form no
// modo edição, pra não mostrar "5000,00" sem separador de milhar enquanto o campo normal
// mostraria "5.000,00" pro mesmo valor.
// [FIX 01/set/2026] Sempre mostra as duas casas de centavo, mesmo quando o valor salvo no
// banco não tinha decimal nenhum (ex: registros antigos gravados como "67588" puro, sem
// ",00" — sequela do bug de máscara já corrigido). Antes, um valor sem parte decimal vinha
// sem vírgula nenhuma ("67.588"), dando a entender que não tinha centavo pra editar.
function formatRawCostForDisplay(raw) {
  if (raw === null || raw === undefined || raw === '') return '';
  const [rawInt, rawDec] = String(raw).split('.');
  let intPart = (rawInt || '').replace(/\D/g, '');
  intPart = intPart.replace(/^0+/, '') || '0';
  intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const decDigits = (rawDec || '').replace(/\D/g, '').slice(0, 2).padEnd(2, '0');
  return `${intPart},${decDigits}`;
}

// Converte o valor mascarado do campo (ex: "5.000,00", onde "." é separador de milhar e ","
// é o decimal) de volta pro formato que parseFloat entende (ex: "5000.00"). Precisa remover
// TODOS os pontos de milhar antes de trocar a vírgula por ponto — trocar só a vírgula deixa
// dois pontos na string ("5.000.00") e parseFloat para de ler no segundo ponto, devolvendo 5
// em vez de 5000. Foi exatamente esse o bug reportado (custo de R$ 5.000 salvando como R$ 5).
function parseMaskedCost(masked) {
  if (!masked) return null;
  const normalized = masked.replace(/\./g, '').replace(',', '.');
  const value = parseFloat(normalized);
  return Number.isNaN(value) ? null : value;
}

export default function ServiceModal({ open, onClose, vehicles, onSuccess, editService = null }) {
  const isEdit = !!editService;
  const [serviceTypes, setServiceTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [proSuggestions, setProSuggestions] = useState([]);
  const [proSearching, setProSearching] = useState(false);
  const [showProDropdown, setShowProDropdown] = useState(false);
  const proTimerRef = React.useRef(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  // Load service types + pre-fill form
  useEffect(() => {
    if (!open) return;

    servicesAPI.types()
      .then(r => {
        const types = r.types || r.data?.types || [];
        setServiceTypes(types.length > 0 ? types : FALLBACK_TYPES);
      })
      .catch(() => setServiceTypes(FALLBACK_TYPES));

    if (editService) {
      // Modo edição: preenche o form com os dados do serviço
      const costStr = editService.cost != null ? formatRawCostForDisplay(editService.cost) : '';
      setForm({
        vehicle_id: editService.vehicle_id || '',
        service_type_id: editService.service_type_id || (editService.custom_service_name ? 'custom' : ''),
        custom_service_name: editService.custom_service_name || '',
        performed_at: editService.performed_at ? editService.performed_at.split('T')[0] : '',
        km_at_service: editService.km_at_service ? String(editService.km_at_service) : '',
        cost: costStr,
        professional_name: editService.professional_name || '',
        professional_id: editService.professional_id || null,
        notes: editService.notes || '',
        photo_url: editService.photo_url || '',
        video_url: editService.video_url || '',
      });
    } else {
      // Modo criação: form limpo, pré-seleciona primeiro veículo
      const v = vehicles[0];
      setForm({
        ...emptyForm,
        vehicle_id: v?.id || '',
        km_at_service: String(v?.current_km || ''),
      });
    }
  }, [open, editService]);

  if (!open) return null;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleProSearch = (value) => {
    set('professional_name', value);
    set('professional_id', null);
    if (proTimerRef.current) clearTimeout(proTimerRef.current);
    if (value.length < 2) { setProSuggestions([]); setShowProDropdown(false); return; }
    proTimerRef.current = setTimeout(() => {
      setProSearching(true);
      professionalsAPI.search({ q: value })
        .then(r => {
          setProSuggestions((r.professionals || []).slice(0, 5));
          setShowProDropdown(true);
        })
        .catch(() => {})
        .finally(() => setProSearching(false));
    }, 300);
  };

  const selectPro = (pro) => {
    setForm(f => ({ ...f, professional_name: pro.business_name || pro.user_name, professional_id: pro.id }));
    setShowProDropdown(false);
    setProSuggestions([]);
  };

  const selectedVehicle = vehicles.find(v => v.id === form.vehicle_id);
  const selectedType = serviceTypes.find(t => t.id === form.service_type_id);
  const isCustom = form.service_type_id === 'custom';

  // ── KM mask ──
  const formatKm = (raw) => {
    const digits = String(raw).replace(/\D/g, '');
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };
  const handleKmChange = (value) => {
    const digits = value.replace(/\D/g, '');
    set('km_at_service', digits);
  };
  const kmDisplay = formatKm(form.km_at_service || '');
  const kmNum = parseInt(form.km_at_service) || 0;
  const vehicleCurrentKm = selectedVehicle?.current_km || 0;
  const kmWarning = kmNum > 0 && vehicleCurrentKm > 0 && kmNum < vehicleCurrentKm;

  // ── Cost mask ──
  const handleCostChange = (value) => {
    // Só permite dígitos e vírgula, máx 2 decimais
    const clean = value.replace(/[^\d,]/g, '');
    const parts = clean.split(',');
    // Formata parte inteira com pontos de milhar
    let intPart = (parts[0] || '').replace(/\D/g, '');
    if (intPart.length > 0) {
      intPart = intPart.replace(/^0+/, '') || '0';
      intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
    const decPart = parts.length > 1 ? ',' + (parts[1] || '').slice(0, 2) : '';
    set('cost', intPart + decPart);
  };

  const handleVehicleChange = (vehicleId) => {
    const v = vehicles.find(x => x.id === vehicleId);
    set('vehicle_id', vehicleId);
    if (v && !isEdit) set('km_at_service', String(v.current_km || ''));
  };

  // ── Upload de mídia (foto/vídeo de comprovação) ──
  // Fluxo: pede uma URL assinada pro backend → sobe o arquivo DIRETO pro Supabase
  // Storage (fetch PUT) → guarda a URL pública no form. O arquivo nunca passa pelo
  // nosso servidor.
  const uploadToStorage = async (file) => {
    const { uploadUrl, publicUrl } = await servicesAPI.getUploadUrl(file.type);
    const putRes = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!putRes.ok) throw new Error('Falha no envio do arquivo');
    return publicUrl;
  };

  const handlePhotoSelect = async (file) => {
    if (!file) return;
    if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
      toast.error('Formato de imagem não suportado (use JPG, PNG ou WEBP)');
      return;
    }
    if (file.size > MAX_PHOTO_MB * 1024 * 1024) {
      toast.error(`Foto muito grande — máximo ${MAX_PHOTO_MB}MB`);
      return;
    }
    setUploadingPhoto(true);
    try {
      const url = await uploadToStorage(file);
      set('photo_url', url);
    } catch (err) {
      toast.error(err.message || 'Erro ao enviar foto');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleVideoSelect = async (file) => {
    if (!file) return;
    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
      toast.error('Formato de vídeo não suportado (use MP4 ou MOV)');
      return;
    }
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
      toast.error(`Vídeo muito grande — máximo ${MAX_VIDEO_MB}MB`);
      return;
    }
    try {
      const duration = await readVideoDuration(file);
      if (duration > MAX_VIDEO_SECONDS) {
        toast.error(`Vídeo muito longo — máximo ${MAX_VIDEO_SECONDS} segundos`);
        return;
      }
    } catch {
      toast.error('Não foi possível processar o vídeo');
      return;
    }
    setUploadingVideo(true);
    try {
      const url = await uploadToStorage(file);
      set('video_url', url);
    } catch (err) {
      toast.error(err.message || 'Erro ao enviar vídeo');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.vehicle_id) { toast.error('Selecione um veículo'); return; }
    if (!form.service_type_id && !form.custom_service_name) { toast.error('Selecione o tipo de serviço'); return; }
    if (isCustom && !form.custom_service_name) { toast.error('Informe o nome do serviço'); return; }
    if (!form.performed_at) { toast.error('Informe a data'); return; }
    if (!form.km_at_service) { toast.error('Informe a quilometragem'); return; }

    setSaving(true);
    try {
      const payload = {
        vehicle_id: form.vehicle_id,
        service_type_id: isCustom ? null : form.service_type_id || null,
        custom_service_name: isCustom ? form.custom_service_name : null,
        performed_at: form.performed_at,
        km_at_service: parseInt(form.km_at_service) || 0,
        cost: parseMaskedCost(form.cost),
        professional_name: form.professional_name || null,
        professional_id: form.professional_id || null,
        notes: form.notes || null,
        photo_url: form.photo_url || null,
        video_url: form.video_url || null,
      };

      if (isEdit) {
        await servicesAPI.update(editService.id, payload);
        toast.success('Serviço atualizado! ✏️');
      } else {
        await servicesAPI.create(payload);
        toast.success('Serviço registrado! 🔧');
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.message || (isEdit ? 'Erro ao atualizar serviço' : 'Erro ao registrar serviço'));
    } finally {
      setSaving(false);
    }
  };

  const vehicleEmoji = { car: '🚗', motorcycle: '🏍️', truck: '🛻', suv: '🚙' };

  const chipLabels = {
    'Troca de óleo': 'Óleo',
    'Troca de pneu': 'Pneus',
    'Alinhamento e balanceamento': 'Pneus',
  };

  return (
    <div style={M.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={M.modal}>
        {/* Header */}
        <div style={M.head}>
          <h2 style={M.title}>{isEdit ? 'Editar serviço' : 'Registrar serviço'}</h2>
          <button style={M.close} onClick={onClose}
            onMouseOver={e => { e.currentTarget.style.background = '#c94a2f'; e.currentTarget.style.color = '#fff'; }}
            onMouseOut={e => { e.currentTarget.style.background = '#f4ede0'; e.currentTarget.style.color = '#183d24'; }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Vehicle selector */}
        <div style={{ ...M.row, marginBottom: '16px' }}>
          <label style={M.label}>Veículo</label>
          <select
            style={{ ...M.select, opacity: isEdit ? 0.6 : 1 }}
            value={form.vehicle_id}
            onChange={e => handleVehicleChange(e.target.value)}
            disabled={isEdit}
          >
            <option value="">Selecionar veículo</option>
            {vehicles.map(v => (
              <option key={v.id} value={v.id}>
                {vehicleEmoji[v.type] || '🚗'} {v.nickname || `${v.brand} ${v.model}`} {v.year} {v.plate ? `· ${v.plate}` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Service type chips */}
        <div style={{ ...M.row, marginBottom: '16px' }}>
          <label style={M.label}>Tipo de serviço</label>
          <div style={M.chipRow}>
            {serviceTypes.slice(0, 6).map(t => (
              <Chip
                key={t.id}
                label={chipLabels[t.name] || t.name}
                icon={getServiceTypeIcon(t.name)}
                selected={form.service_type_id === t.id}
                onClick={() => set('service_type_id', form.service_type_id === t.id ? '' : t.id)}
              />
            ))}
            <Chip
              label="Outro"
              icon={TYPE_ICON_SVGS.plus}
              selected={isCustom}
              onClick={() => set('service_type_id', isCustom ? '' : 'custom')}
            />
          </div>
          {isCustom && (
            <input
              style={{ ...M.input, marginTop: '8px' }}
              placeholder="Nome do serviço"
              value={form.custom_service_name}
              onChange={e => set('custom_service_name', e.target.value)}
              autoFocus
            />
          )}
        </div>

        {/* Form grid */}
        <div style={M.grid}>
          <div style={M.row}>
            <label style={M.label}>Data</label>
            <input
              type="date"
              style={M.input}
              value={form.performed_at}
              max={new Date().toISOString().split('T')[0]}
              onChange={e => set('performed_at', e.target.value)}
            />
          </div>
          <div style={M.row}>
            <label style={M.label}>Quilometragem</label>
            <input
              style={M.input}
              placeholder="67.450"
              value={kmDisplay}
              onChange={e => handleKmChange(e.target.value)}
              inputMode="numeric"
            />
            {kmWarning && (
              <span style={{ fontSize: '12px', color: '#e97117', marginTop: '4px', display: 'block' }}>
                ⚠️ Km inferior ao último registro ({formatKm(vehicleCurrentKm)} km)
              </span>
            )}
          </div>
          <div style={M.row}>
            <label style={M.label}>Custo (R$)</label>
            <input
              style={M.input}
              placeholder="285,00"
              value={form.cost}
              onChange={e => handleCostChange(e.target.value)}
              inputMode="decimal"
            />
          </div>
          <div style={M.row}>
            <label style={M.label}>Profissional {proSearching && <span style={{ fontSize: '10px', opacity: 0.6 }}>⏳</span>}</label>
            <div style={{ position: 'relative' }}>
              <input
                style={M.input}
                placeholder="Buscar oficina ou digitar nome"
                value={form.professional_name}
                onChange={e => handleProSearch(e.target.value)}
                onFocus={() => { if (proSuggestions.length > 0) setShowProDropdown(true); }}
                onBlur={() => setTimeout(() => setShowProDropdown(false), 200)}
              />
              {form.professional_id && (
                <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: '#14562e', color: '#fff', fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '999px' }}>✓ NICO</span>
              )}
              {showProDropdown && proSuggestions.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e5ddc9', borderRadius: '10px', marginTop: '4px', zIndex: 10, boxShadow: '0 8px 24px rgba(15,64,35,.12)', overflow: 'hidden' }}>
                  {proSuggestions.map(pro => (
                    <div key={pro.id} onMouseDown={() => selectPro(pro)}
                      style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f4ede0', transition: 'background 0.1s', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      onMouseOver={e => { e.currentTarget.style.background = '#f9f5ec'; }}
                      onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#14562e' }}>
                          {pro.business_name || pro.user_name}
                          {pro.is_verified && <span style={{ background: '#14562e', color: '#fff', fontSize: '9px', fontWeight: 700, padding: '1px 5px', borderRadius: '999px', marginLeft: '6px' }}>✓</span>}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6a7268' }}>
                          {pro.address_city}{pro.address_state ? `, ${pro.address_state}` : ''} {pro.specialty ? `· ${pro.specialty}` : ''}
                        </div>
                      </div>
                      {pro.rating_avg > 0 && <span style={{ fontSize: '12px', color: '#e97117', fontWeight: 700 }}>★ {pro.rating_avg.toFixed(1)}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div style={{ ...M.row, gridColumn: '1 / -1' }}>
            <label style={M.label}>Observações</label>
            <textarea
              style={M.textarea}
              placeholder="Óleo 5W30 sintético + filtro. Recomendou troca de vela em 5.000 km."
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
            />
          </div>

          {/* Foto/vídeo de comprovação (opcional) */}
          <div style={{ ...M.row, gridColumn: '1 / -1' }}>
            <label style={M.label}>Foto do reparo (opcional)</label>
            {form.photo_url ? (
              <div style={{ position: 'relative', width: '96px' }}>
                <img
                  src={form.photo_url}
                  alt="Foto do serviço"
                  style={{ width: '96px', height: '96px', objectFit: 'cover', borderRadius: '10px', border: '1.5px solid #e5ddc9', display: 'block' }}
                />
                <button
                  type="button"
                  onClick={() => set('photo_url', '')}
                  style={{ position: 'absolute', top: '-8px', right: '-8px', width: '22px', height: '22px', borderRadius: '50%', background: '#c94a2f', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <label
                style={{
                  ...M.btn, background: '#f4ede0', color: '#183d24', display: 'inline-flex',
                  cursor: uploadingPhoto ? 'wait' : 'pointer', opacity: uploadingPhoto ? 0.6 : 1, width: 'fit-content',
                }}
              >
                {uploadingPhoto ? 'Enviando...' : <>{TYPE_ICON_SVGS.camera} Adicionar foto</>}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  capture="environment"
                  style={{ display: 'none' }}
                  onChange={e => { handlePhotoSelect(e.target.files?.[0]); e.target.value = ''; }}
                  disabled={uploadingPhoto}
                />
              </label>
            )}
          </div>

          <div style={{ ...M.row, gridColumn: '1 / -1' }}>
            <label style={M.label}>Vídeo do reparo (opcional, até {MAX_VIDEO_SECONDS}s)</label>
            {form.video_url ? (
              <div style={{ position: 'relative', width: '220px' }}>
                <video
                  src={form.video_url}
                  controls
                  style={{ width: '220px', borderRadius: '10px', border: '1.5px solid #e5ddc9', display: 'block' }}
                />
                <button
                  type="button"
                  onClick={() => set('video_url', '')}
                  style={{ position: 'absolute', top: '-8px', right: '-8px', width: '22px', height: '22px', borderRadius: '50%', background: '#c94a2f', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <label
                style={{
                  ...M.btn, background: '#f4ede0', color: '#183d24', display: 'inline-flex',
                  cursor: uploadingVideo ? 'wait' : 'pointer', opacity: uploadingVideo ? 0.6 : 1, width: 'fit-content',
                }}
              >
                {uploadingVideo ? 'Enviando...' : <>{TYPE_ICON_SVGS.video} Adicionar vídeo</>}
                <input
                  type="file"
                  accept="video/mp4,video/quicktime"
                  style={{ display: 'none' }}
                  onChange={e => { handleVideoSelect(e.target.files?.[0]); e.target.value = ''; }}
                  disabled={uploadingVideo}
                />
              </label>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={M.actions}>
          <button
            style={{ ...M.btn, background: 'transparent', color: '#183d24' }}
            onClick={onClose}
            onMouseOver={e => { e.currentTarget.style.background = '#ede4d1'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            Cancelar
          </button>
          <button
            style={{ ...M.btn, background: isEdit ? '#e97117' : '#14562e', color: '#fff', opacity: saving ? 0.6 : 1 }}
            onClick={handleSubmit}
            disabled={saving}
            onMouseOver={e => { if (!saving) e.currentTarget.style.opacity = '0.85'; }}
            onMouseOut={e => { e.currentTarget.style.opacity = saving ? '0.6' : '1'; }}
          >
            {saving ? (
              <span className="spinner" style={{ width: '16px', height: '16px', borderTopColor: 'white' }} />
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                {isEdit ? 'Salvar alterações' : 'Salvar e enviar para validação'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
